import { ImportErrorResponse } from "./rosterImportApiSlice";
import { ImportProgress } from "./ImportProgressBar";

type ImportStep =
	| "importCourses"
	| "importTeachers"
	| "prepareImportStudents"
	| "resolveStudentConflicts"
	| "importStudents"
	| "complete";

export const stepProgress: Record<ImportStep, ImportProgress> = {
	importCourses: {
		message: "Importing courses",
		phase: "Course Import",
		percent: 0,
	},
	importTeachers: {
		message: "Importing teachers",
		phase: "Teacher Import",
		percent: 20,
	},
	prepareImportStudents: {
		message: "Preparing students",
		phase: "Student Preparation",
		percent: 40,
	},
	resolveStudentConflicts: {
		message: "Prepared students require attention...",
		phase: "Action Required",
		percent: 60,
		pause: true,
	},
	importStudents: {
		message: "Importing students",
		phase: "Student Import",
		percent: 99,
	},
	complete: {
		message: "Roster import finished.",
		phase: "Complete",
		percent: 100,
	},
};

export function isImportError(response: unknown): response is ImportErrorResponse {
	return (
		typeof response === "object" &&
		response !== null &&
		"dataProcessingSuccessful" in response &&
		(response as ImportErrorResponse).dataProcessingSuccessful === false
	);
}

export function chunk<T>(items: T[], size: number): T[][] {
	if (size <= 0) throw new Error("chunk size must be > 0");

	const out: T[][] = [];
	for (let i = 0; i < items.length; i += size) {
		out.push(items.slice(i, i + size));
	}
	return out;
}

export type BatchProgress = {
	current: number;
	total: number;
	percent: number;
};

export type BatchProcessor<T, TResult> = (args: {
	batch: T[];
	batchIndex: number;
	batchCount: number;
}) => Promise<TResult>;

export async function processInBatches<T, TResult>({
	items,
	batchSize,
	processor,
	onProgress,
	progressRange,
	progressMode = "items",
	shouldCancel,
}: {
	items: T[];
	batchSize: number;
	processor: BatchProcessor<T, TResult>;
	onProgress?: (p: BatchProgress) => void;
	progressRange?: { startPercent: number; endPercent: number };
	progressMode?: "batches" | "items";
	shouldCancel?: () => boolean;
}): Promise<TResult[]> {
	const batches = chunk(items, batchSize);
	const results: TResult[] = [];

	// Clamp to sane bounds
	const start = Math.max(0, Math.min(100, progressRange?.startPercent ?? 0));
	const end = Math.max(0, Math.min(100, progressRange?.endPercent ?? 100));
	const span = Math.max(0, end - start);

	let processedItems = 0;

	const report = (completedBatches: number) => {
		const total =
			progressMode === "batches" ? Math.max(1, batches.length) : Math.max(1, items.length);

		const done = progressMode === "batches" ? completedBatches : processedItems;

		const fraction = Math.max(0, Math.min(1, done / total));
		const percent = Math.round(start + span * fraction);

		onProgress?.({
			current: processedItems,
			total: items.length,
			percent,
		});
	};

	// Initial progress
	report(0);

	for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
		if (shouldCancel?.()) break; // cancellation returns partial results

		const batch = batches[batchIndex];
		const result = await processor({
			batch,
			batchIndex,
			batchCount: batches.length,
		});

		// If cancel happened while awaiting the processor, don’t record or report it.
		if (shouldCancel?.()) break;

		results.push(result);
		processedItems += batch.length;

		report(batchIndex + 1);
	}

	// Only go to endPercent if we completed all batches.
	if (results.length === batches.length) {
		onProgress?.({
			current: items.length,
			total: items.length,
			percent: Math.round(end),
		});
	}

	return results;
}
